import React, { useEffect, useMemo, useState } from "react";
import { getActivity } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Clock3, PlusCircle, RotateCcw, UserPlus, Check } from "lucide-react";
import { TimelineSkeleton } from "../components/Skeleton";

const FILTERS = [
  { key: "all", label: "All", types: null },
  { key: "captured", label: "Captured", types: ["CREATED"] },
  { key: "brought_back", label: "Brought back", types: ["RESURFACED"] },
  { key: "passed_on", label: "Passed on", types: ["ASSIGNED"] },
  { key: "done", label: "Done", types: ["DONE"] },
];

const VISUAL = {
  CREATED: { icon: PlusCircle, dot: "bg-[var(--pine)]" },
  RESURFACED: { icon: RotateCcw, dot: "bg-[var(--amber)]" },
  ASSIGNED: { icon: UserPlus, dot: "bg-[var(--slate)]" },
  DONE: { icon: Check, dot: "bg-[var(--pine)]" },
};

function dayLabel(iso) {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff > 1 && diff < 7) return new Date(iso).toLocaleDateString(undefined, { weekday: "long" });
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function timeOnly(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function groupByDay(events) {
  const groups = [];
  let cur = null;
  for (const ev of events) {
    const label = dayLabel(ev.created_at);
    if (!cur || cur.label !== label) { cur = { label, items: [] }; groups.push(cur); }
    cur.items.push(ev);
  }
  return groups;
}

export default function Timeline() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);  // Activity always opens at the top
    getActivity().then(setEvents).finally(() => setLoading(false));
  }, []);

  // Factual, calm sentence. No celebration, no overclaim — just what happened.
  const line = (ev) => {
    const mine = ev.actor_id && user?.id && ev.actor_id === user.id;
    const who = mine ? "You" : (ev.actor_name || "Someone");
    const t = `“${ev.title}”`;
    switch (ev.event_type) {
      case "CREATED": return `${who} captured ${t}`;
      case "RESURFACED": return `Heldall brought back ${t}`;
      case "ASSIGNED": {
        const to = mine ? (user?.partner?.name || "your partner") : "you";
        let base = ev.is_return ? `${who} gave ${t} back` : `${who} gave ${t} to ${to}`;
        if (ev.note) base += `: ${ev.note}`;
        return base;
      }
      case "SEEN": return ev.title;  // never surfaced: no view/seen/read audit of the partner
      case "DONE": return `${who} marked ${t} done`;
      default: return ev.title;
    }
  };

  const counts = useMemo(() => {
    const c = { all: events.length };
    FILTERS.forEach((f) => { if (f.types) c[f.key] = events.filter((e) => f.types.includes(e.event_type)).length; });
    return c;
  }, [events]);

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f || !f.types) return events;
    return events.filter((e) => f.types.includes(e.event_type));
  }, [events, filter]);

  const chip = (active) =>
    `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
      active ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]" : "text-[var(--ink3)] hover:text-[var(--ink2)]"
    }`;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="mb-7">
        <h1 className="font-heading font-serif-display text-4xl tracking-tight font-medium text-[var(--ink)]">Activity</h1>
        <p className="text-[var(--ink3)] mt-2">A clear record of what was held, brought back, and handled.</p>
      </div>

      <div className="flex flex-wrap items-center gap-1 rounded-full bg-[var(--line2)] p-1 mb-8 w-fit" data-testid="activity-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`activity-filter-${f.key}`}
            className={chip(filter === f.key)}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {counts[f.key] > 0 && (
              <span className={`text-xs font-bold rounded-full px-1.5 py-0.5 ${filter === f.key ? "bg-[var(--line2)] text-[var(--ink2)]" : "bg-[var(--surface)] text-[var(--ink4)]"}`}>
                {counts[f.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <TimelineSkeleton />
      ) : visible.length === 0 ? (
        <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] p-12 text-center" data-testid="activity-empty">
          <Clock3 className="mx-auto text-[var(--pine-line)] mb-3" size={30} strokeWidth={1.4} />
          <p className="text-[var(--ink3)]">
            {filter === "all"
              ? "Nothing here yet. Everything you capture leaves a trace right here."
              : "Nothing to show in this view yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-9" data-testid="timeline-list">
          {groupByDay(visible).map((group) => (
            <section key={group.label}>
              <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--ink4)] mb-4">{group.label}</h2>
              <div className="relative pl-6">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[var(--line)]" />
                <div className="space-y-3">
                  {group.items.map((ev) => {
                    const v = VISUAL[ev.event_type] || { icon: Clock3, dot: "bg-[var(--ink3)]" };
                    const Icon = v.icon;
                    return (
                      <div key={ev.id} className="relative" data-testid={`timeline-item-${ev.id}`}>
                        <span className={`absolute -left-[22px] top-3 h-3.5 w-3.5 rounded-full ring-4 ring-[var(--bg)] ${v.dot}`} />
                        <div className="heldall-card bg-[var(--surface)] border border-[var(--line)] p-4 flex items-center gap-3 transition-all duration-200 hover:shadow-[0_8px_28px_rgba(44,40,37,0.09)]">
                          <span className="h-8 w-8 rounded-full bg-[var(--surface2)] flex items-center justify-center text-[var(--ink2)] shrink-0">
                            <Icon size={15} strokeWidth={1.9} />
                          </span>
                          <p className="text-[15px] text-[var(--ink)] leading-snug flex-1 min-w-0">{line(ev)}</p>
                          <span className="text-xs text-[var(--ink5)] shrink-0">{timeOnly(ev.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
